/** Design tokens aligned with new_ui/MVKG prototype (MUI-compatible). */

export const mvkgTokens = {
  brand: {
    main: '#4f46e5',   // indigo 600
    light: '#6366f1',  // indigo 500
    dark: '#3730a3',   // indigo 700
    contrastText: '#ffffff',
  },
  surface: {
    default: '#fafafb',
    paper: '#ffffff',
    // Prototype glass: solid-ish (no heavy backdrop blur) to avoid Chrome
    // backdrop-filter artefacts on lists/canvas at scale.
    glass: 'rgba(255, 255, 255, 0.88)',
    glassStrong: 'rgba(255, 255, 255, 0.96)',
    glassSoft: 'rgba(255, 255, 255, 0.62)',
    glassBorder: 'rgba(226, 232, 240, 0.7)',
    glassBorderStrong: 'rgba(226, 232, 240, 0.8)',
  },
  radius: {
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24, // rounded-3xl on cards
  },
  shadow: {
    brand: '0 12px 32px -8px rgba(79, 70, 229, 0.25)',
    brandSm: '0 6px 20px -6px rgba(79, 70, 229, 0.2)',
    card: '0 1px 2px rgba(15, 23, 42, 0.04)',
    cardStrong: '0 1px 3px rgba(15, 23, 42, 0.06)',
    glass: '0 1px 3px rgba(15, 23, 42, 0.06)',
    pop: '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
  },
  font: {
    display: '"Inter", "DM Sans", "Roboto", sans-serif',
    body: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    mono: '"JetBrains Mono", "Roboto Mono", monospace',
  },
  gradient: {
    brand: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    // 3 radial brand/purple washes over the off-white base (prototype body bg).
    page:
      'radial-gradient(ellipse 80% 50% at 0% 0%, rgba(99,102,241,0.08), transparent 60%),' +
      'radial-gradient(ellipse 60% 50% at 100% 100%, rgba(168,85,247,0.06), transparent 60%),' +
      'radial-gradient(ellipse 50% 40% at 50% 100%, rgba(99,102,241,0.04), transparent 70%)',
  },
};

/**
 * Accent surface palette — soft bg + readable fg per semantic colour.
 * Mirrors the prototype's `bg-{color}-50 / text-{color}-700` chip vocabulary.
 */
export const accentSurface = {
  brand:   { bg: 'rgba(99,102,241,0.10)',  fg: '#4338ca' },
  emerald: { bg: 'rgba(16,185,129,0.12)',  fg: '#047857' },
  amber:   { bg: 'rgba(245,158,11,0.14)',  fg: '#b45309' },
  rose:    { bg: 'rgba(244,63,94,0.12)',   fg: '#be123c' },
  purple:  { bg: 'rgba(168,85,247,0.12)',  fg: '#7e22ce' },
  pink:    { bg: 'rgba(236,72,153,0.12)',  fg: '#be185d' },
  blue:    { bg: 'rgba(59,130,246,0.12)',  fg: '#1d4ed8' },
  cyan:    { bg: 'rgba(6,182,212,0.12)',   fg: '#0e7490' },
  sky:     { bg: 'rgba(14,165,233,0.12)',  fg: '#0369a1' },
  gray:    { bg: 'rgba(100,116,139,0.12)', fg: '#334155' },
};

/**
 * The reading dialect used on /digest (Review, the PDF reader's panels and
 * popovers): white cards on hairline rules, slate ink, three radii. The rest of
 * the app keeps the glass look above.
 */
export const readerTokens = {
  ink: '#0f172a',
  body: '#1e293b',
  muted: '#475569',
  faint: '#64748b', // meta and captions; 4.8:1 on white
  rule: 'rgba(15,23,42,0.08)',
  ruleStrong: 'rgba(15,23,42,0.14)',
  wash: '#f8fafc',
  chip: '#f1f5f9',
  hover: 'rgba(15,23,42,0.05)', // quiet row / icon hover
  stage: '#f4f4f5', // the grey the PDF pages sit on
  radius: { xs: 6, sm: 8, md: 12 }, // tag · control · card/popover
  size: { meta: 12, ui: 13, body: 14, prose: 16, h3: 15, h2: 18, h1: 34 },
};

/**
 * The app shell — sidebar, top bar, dialogs and menus — in the reader dialect,
 * so the frame around every page matches Home, People, the paper page and the
 * entity hub instead of the older glass look. Flat white on a hairline rule;
 * the active nav item is a grey fill with ink text and an indigo icon (no solid
 * indigo block, no glow); group labels are small sentence-case text, not mono
 * capitals; dialogs and popovers take the card radius (12), not 40px pills.
 */
export const shellTokens = {
  sidebarWidth: 240,
  sidebarCollapsed: 64,
  surface: '#ffffff',
  rule: readerTokens.rule,
  nav: {
    height: 36,
    radius: readerTokens.radius.sm,
    text: readerTokens.body,
    icon: readerTokens.muted,
    hover: readerTokens.hover,
    activeBg: readerTokens.chip,
    activeText: readerTokens.ink,
    activeIcon: mvkgTokens.brand.main,
  },
  // Group label: 12/600 sentence case, faint. Used sparingly (one per group
  // that needs naming), never as an uppercase mono overline.
  label: { size: readerTokens.size.meta, weight: 600, color: readerTokens.faint },
  pageTitle: { size: 15, weight: 600, color: readerTokens.ink },
  controlRadius: readerTokens.radius.sm,
  dialogRadius: readerTokens.radius.md,
};

export const glassSx = (strong = false) => ({
  background: strong ? mvkgTokens.surface.glassStrong : mvkgTokens.surface.glass,
  border: `1px solid ${strong ? mvkgTokens.surface.glassBorderStrong : mvkgTokens.surface.glassBorder}`,
  boxShadow: strong ? mvkgTokens.shadow.cardStrong : mvkgTokens.shadow.card,
});
